import React, { memo, useReducer, useCallback } from 'react';
import { View } from 'react-native';
import { createUseStyle } from '@redlink/assets/styles';
import {
  DataGrid,
  DataGridActionsColumn,
  DataGridSimpleTextColumn,
  DataGridTextLinkColumn,
  DataGridTagsColumn,
  DataGridTileColumn,
  DataGridDateTimeColumn,
  Pagination,
  ListHeader,
} from '@redlink/components/muscles';
import {
  WebScrollView,
  IconCrown,
  IconEdit,
  IconArrowLeft,
  Space,
  Card,
  Text,
  IconSearch,
  IconClose,
  LoadingContainer,
} from '@redlink/components/atoms';
import { TextInput, Button } from '@redlink/components/molecules';
import { useLocale } from '@redlink/assets/locale';
import { useValues } from '@redlink/assets/values';
import { AdminNewUserPage } from '@redlink/components/pages';
import { useQuery, getUsersQuery, cache } from '@redlink/services';
import { initialState, reducer, types } from './store';

const PAGE_SIZE = 10;

export const AdminUsersPage = memo(({ navigation }) => {
  const { styles, colors } = useStyle();
  const { dictionary } = useLocale();
  const { constants, staticData } = useValues();

  const [
    {
      searchedUsername,
      searchedPageName,
      newUserIsVisible,
      currentUser,
      fetchMoreIsBusy,
      currentPageIndex,
      sortKey,
      sortDirection,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  const {
    fetchMore,
    loading: getUsersIsBusy,
    data: { Users: { totalCount = 0, items: Users = [] } = {} },
  } = useQuery(getUsersQuery, {
    variables: {
      skip: 0,
      take: PAGE_SIZE,
    },
  });

  const fetchMoreUsers = useCallback(
    ({
      pageIndex = currentPageIndex,
      username = searchedUsername,
      pageName = searchedPageName,
      sortBy = sortKey,
      sortByDirection = sortDirection,
    }) => {
      dispatch({
        type: types.SET_FETCH_MORE_IS_BUSY,
        payload: true,
      });
      fetchMore({
        variables: {
          skip: pageIndex * PAGE_SIZE,
          take: PAGE_SIZE,
          sortKey: sortBy,
          sortDirection: sortByDirection,
          ...(username && { username: searchedUsername }),
          ...(pageName && { pageName: searchedPageName }),
        },
        updateQuery: (prev, { fetchMoreResult, variables }) => {
          dispatch({
            type: types.SET_FETCH_MORE_IS_BUSY,
            payload: false,
          });

          if (!fetchMoreResult) return prev;

          const newResult = {
            ...prev,
            Users: fetchMoreResult.Users,
          };

          cache.writeQuery({
            query: getUsersQuery,
            data: newResult,
            variables,
          });

          return newResult;
        },
      });
    },
    [
      currentPageIndex,
      fetchMore,
      searchedPageName,
      searchedUsername,
      sortDirection,
      sortKey,
    ]
  );

  const removeFilter = useCallback(() => {
    dispatch({
      type: types.SET_MULTI,
      payload: {
        currentPageIndex: 0,
        searchedUsername: '',
        searchedPageName: '',
      },
    });
    fetchMoreUsers({ pageIndex: 0, username: '', pageName: '' });
  }, [fetchMoreUsers]);

  const filter = useCallback(() => {
    dispatch({
      type: types.SET_CURRENT_PAGE_INDEX,
      payload: 0,
    });
    fetchMoreUsers({ pageIndex: 0 });
  }, [fetchMoreUsers]);

  const filterIsEnabled = searchedUsername !== '' || searchedPageName !== '';

  return (
    <View style={styles.container}>
      <WebScrollView>
        <ListHeader
          title={dictionary.users}
          actions={[
            {
              title: dictionary.addNewUser,
              variant: 'primary',
              icon: <IconEdit color={colors.white} size={20} />,
              onPress: () =>
                dispatch({
                  type: types.SET_MULTI,
                  payload: {
                    currentUser: null,
                    newUserIsVisible: true,
                  },
                }),
            },
          ]}
        />
        <Space />
        <Card style={styles.searchPanelContainer}>
          <View style={styles.searchHeaderContainer}>
            <Text style={styles.headerTitleText}>{dictionary.deepSearch}</Text>
          </View>
          <View style={styles.bodyContainer}>
            <View style={styles.bodyContentContainer}>
              <TextInput
                en
                value={searchedUsername}
                title={dictionary.email}
                onChangeText={(value) =>
                  dispatch({
                    type: types.SET_SEARCHED_USERNAME,
                    payload: value,
                  })
                }
                style={styles.input}
              />
              <Space />
              <TextInput
                en
                title={dictionary.pageName}
                prefix={constants.pageLinkPrefix}
                value={searchedPageName}
                onChangeText={(value) =>
                  dispatch({
                    type: types.SET_SEARCHED_PAGENAME,
                    payload: value,
                  })
                }
                style={styles.input}
              />
            </View>
            <View style={styles.actionsContainer}>
              <Button
                disabled={!filterIsEnabled}
                style={styles.filterButton}
                variant="secondary"
                title={dictionary.removeFilter}
                icon={<IconClose color={colors.red1} />}
                onPress={removeFilter}
              />
              <Space size={8} />
              <Button
                style={styles.filterButton}
                variant="primary"
                title={dictionary.filter}
                icon={<IconSearch color={colors.white} />}
                onPress={filter}
              />
            </View>
          </View>
        </Card>
        <Space />
        <LoadingContainer isBusy={getUsersIsBusy || fetchMoreIsBusy}>
          <DataGrid
            data={Users}
            sortBy={sortKey}
            sortByDirection={sortDirection}
            onSort={(columnName, direction) => {
              dispatch({
                type: types.SET_MULTI,
                payload: { sortKey: columnName, sortDirection: direction },
              });
              fetchMoreUsers({
                sortBy: columnName,
                sortByDirection: direction,
              });
            }}
          >
            <DataGridSimpleTextColumn
              title={dictionary.name}
              sortable
              sortKey="profile.fullname"
              flex={2}
              fieldName="fullname"
            />
            <DataGridSimpleTextColumn
              title={dictionary.email}
              sortable
              sortKey="username"
              flex={3}
              fieldName="username"
            />
            <DataGridTextLinkColumn
              title={dictionary.account}
              sortable
              sortKey="pageName"
              flex={2}
              fieldName="pageName"
              onPress={({ url }) => window.open(url, '_blank')}
            />
            <DataGridTagsColumn
              title={dictionary.tags}
              flex={2}
              fieldName="tags"
            />
            <DataGridDateTimeColumn
              title={dictionary.registerDate}
              sortable
              sortKey="createdDate"
              flex={2}
              fieldName="createdDate"
            />
            <DataGridDateTimeColumn
              title={dictionary.expireDate}
              sortable
              sortKey="plan.expireDate"
              flex={2}
              fieldName="expireDate"
            />
            <DataGridTileColumn
              title={dictionary.accountType}
              sortable
              sortKey="plan.type"
              flex={2}
              fieldName="accountType"
              titleValueExtractor={({ accountType }) => dictionary[accountType]}
              renderImage={({ accountType }) =>
                accountType === staticData.planType.pro && (
                  <IconCrown size={16} />
                )
              }
            />
            <DataGridActionsColumn
              title={dictionary.actions}
              flex={2}
              actions={[
                {
                  onPress: (item) =>
                    dispatch({
                      type: types.SET_MULTI,
                      payload: {
                        currentUser: item,
                        newUserIsVisible: true,
                      },
                    }),
                  renderIcon: (color) => <IconEdit {...{ color }} />,
                },
                {
                  onPress: (item) => {},
                  renderIcon: (color) => <IconArrowLeft {...{ color }} />,
                },
              ]}
            />
          </DataGrid>
        </LoadingContainer>
        <Space size={30} />
        <Pagination
          style={styles.paginationContainer}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          currentPage={currentPageIndex}
          onChange={(pageIndex) => {
            dispatch({
              type: types.SET_CURRENT_PAGE_INDEX,
              payload: pageIndex,
            });
            fetchMoreUsers({ pageIndex });
          }}
        />
      </WebScrollView>
      <AdminNewUserPage
        isVisible={newUserIsVisible}
        onSubmit={() => {
          dispatch({
            type: types.SET_NEW_USER_IS_VISIBLE,
            payload: false,
          });
          fetchMoreUsers({});
        }}
        onDismiss={() =>
          dispatch({
            type: types.SET_NEW_USER_IS_VISIBLE,
            payload: false,
          })
        }
        {...(currentUser && {
          data: currentUser,
        })}
      />
    </View>
  );
});

const useStyle = createUseStyle(({ colors, size }) => ({
  container: {
    flex: 1,
  },
  searchHeaderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  headerTitleText: {
    color: colors.gray23,
    fontSize: 14,
  },
  bodyContainer: {
    backgroundColor: colors.gray7,
    padding: 16,
  },
  bodyContentContainer: {
    flexDirection: 'row',
  },
  input: {
    minWidth: 250,
  },
  searchPanelContainer: {
    padding: 0,
  },
  actionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  filterButton: {
    minWidth: 100,
  },
}));
